/**
 * Seleção e alinhamento dos quadros do loop (lógica pura, testada).
 */
import { HOUR_MS, MINUTE_MS } from '../lib/time';
import type { TimeRange } from '../state/time';

export type PeriodPreset = '1h' | '3h' | '6h' | '12h' | '24h' | '48h';
export type Period = PeriodPreset | 'custom';

export const PERIOD_PRESETS: PeriodPreset[] = ['1h', '3h', '6h', '12h', '24h', '48h'];

export const PERIOD_HOURS: Record<PeriodPreset, number> = {
  '1h': 1,
  '3h': 3,
  '6h': 6,
  '12h': 12,
  '24h': 24,
  '48h': 48,
};

/**
 * Até onde a interface volta no tempo: 48 h antes do quadro mais recente.
 *
 * A NOAA guarda ~10 dias de GOES, mas as outras camadas (chuva, rios, radar,
 * embarcações) só publicam as últimas 24–48 h, e ir além delas exige um
 * servidor próprio que guarde o histórico — que ainda não existe. Um loop de
 * 5 dias atrás mostraria o satélite sozinho, com as camadas vazias.
 */
export const MAX_HISTORY_HOURS = 48;

/** O aviso de quem pede algo mais antigo — no editor do período e nos links. */
export const HISTORY_LIMIT_NOTE = 'Datas mais antigas dependem de um servidor próprio e serão disponibilizadas no futuro.';

/** Os instantes que a interface oferece: só as últimas `MAX_HISTORY_HOURS`. */
export function historyTimes(available: readonly number[], hours = MAX_HISTORY_HOURS): readonly number[] {
  if (available.length === 0) return available;
  const from = available[available.length - 1] - hours * HOUR_MS;
  const i = available.findIndex((t) => t >= from);
  return i <= 0 ? available : available.slice(i);
}

export const STEP_OPTIONS = [10, 20, 30, 60] as const;
export type StepMinutes = (typeof STEP_OPTIONS)[number];

export function isStep(n: number): n is StepMinutes {
  return (STEP_OPTIONS as readonly number[]).includes(n);
}

/**
 * Tolerância para alinhar camadas sobrepostas ao instante da camada base: a
 * MESMA varredura, e só. Menos que a cadência (10 min), como no rastreio
 * (tracking/tempo.ts): cobre carimbos que diferem por arredondamento. Era de
 * 10 min, e quando o STAR não publicava o horário de uma camada sobreposta
 * (5–8% dos quadros em 21/10/2026) ela mostrava a imagem de 10 min antes,
 * sob o contorno do rastreio e sobre a base do horário certo — o satélite
 * parecia fora de sincronia. Agora ela fica escondida nesse quadro.
 */
export const ALIGN_TOLERANCE_MS = 4 * MINUTE_MS;

/**
 * Janela temporal do loop. Períodos relativos terminam no quadro mais recente
 * disponível; o personalizado é recortado ao que existe no servidor.
 */
export function computeWindow(
  available: readonly number[],
  period: Period,
  custom: TimeRange | null,
): TimeRange | null {
  if (available.length === 0) return null;
  const first = available[0];
  const latest = available[available.length - 1];
  if (period === 'custom') {
    if (!custom || !customIntersects(available, custom)) return null;
    const start = Math.max(first, Math.min(custom.start, custom.end));
    const end = Math.min(latest, Math.max(custom.start, custom.end));
    return { start, end };
  }
  return { start: latest - PERIOD_HOURS[period] * HOUR_MS, end: latest };
}

/** O período personalizado tem alguma parte dentro do que está disponível? */
export function customIntersects(available: readonly number[], custom: TimeRange): boolean {
  if (available.length === 0) return false;
  const s = Math.min(custom.start, custom.end);
  const e = Math.max(custom.start, custom.end);
  return e >= available[0] && s <= available[available.length - 1];
}

/**
 * Janela com a mesma duração de `custom`, terminando no quadro mais recente
 * (e sem começar antes do primeiro disponível).
 */
export function latestWindowLike(available: readonly number[], custom: TimeRange): TimeRange {
  const latest = available[available.length - 1];
  const duration = Math.max(10 * MINUTE_MS, Math.abs(custom.end - custom.start));
  return { start: Math.max(available[0], latest - duration), end: latest };
}

/**
 * Quadros do loop: os instantes disponíveis dentro da janela que caem na
 * grade do passo (múltiplos de `step` minutos no relógio UTC — como o passo
 * divide a hora, também no de Brasília). Nos períodos relativos o quadro mais
 * recente entra sempre, mesmo fora da grade, e o início é exclusivo
 * ("últimas 3 h" = 18 quadros de 10 min).
 */
export function selectFrames(
  available: readonly number[],
  window: TimeRange,
  stepMin: number,
  opts: { relative: boolean },
): number[] {
  const inWindow = available.filter((t) => (opts.relative ? t > window.start : t >= window.start) && t <= window.end);
  if (inWindow.length === 0) return [];
  const onGrid = inWindow.filter((t) => Math.round(t / MINUTE_MS) % stepMin === 0);
  if (opts.relative) {
    const latest = inWindow[inWindow.length - 1];
    if (onGrid[onGrid.length - 1] !== latest) onGrid.push(latest);
  }
  return onGrid.length ? onGrid : [inWindow[inWindow.length - 1]];
}

/** Índice do último elemento ≤ t em `sorted` (crescente), ou −1. */
export function lastIndexAtOrBefore(sorted: readonly number[], t: number): number {
  let lo = 0;
  let hi = sorted.length - 1;
  let ans = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (sorted[mid] <= t) {
      ans = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return ans;
}

/**
 * Para cada quadro da base, o instante da camada sobreposta: o mesmo, ou o
 * mais próximo anterior até `toleranceMs`; null se não houver (a camada fica
 * escondida nesse quadro).
 */
export function alignTimes(
  frames: readonly number[],
  times: readonly number[],
  toleranceMs: number = ALIGN_TOLERANCE_MS,
): (number | null)[] {
  return frames.map((t) => {
    const i = lastIndexAtOrBefore(times, t);
    if (i < 0) return null;
    return t - times[i] <= toleranceMs ? times[i] : null;
  });
}

/** Mantém só os `capacity` quadros mais recentes. */
export function truncateToLatest(frames: readonly number[], capacity: number): number[] {
  return frames.length > capacity ? frames.slice(frames.length - capacity) : [...frames];
}

/**
 * Índice a exibir depois que a lista de quadros muda:
 * - `preferTime` (ex.: vindo do link) se existir na nova lista;
 * - se o usuário estava no último quadro (ou não havia quadros), o novo último;
 * - senão o mesmo instante, ou o mais próximo anterior.
 */
export function resolveIndex(
  prevFrames: readonly number[],
  prevIndex: number,
  nextFrames: readonly number[],
  preferTime: number | null = null,
): number {
  if (nextFrames.length === 0) return 0;
  const last = nextFrames.length - 1;
  if (preferTime !== null) {
    const i = nextFrames.indexOf(preferTime);
    if (i >= 0) return i;
  }
  if (prevFrames.length === 0 || prevIndex >= prevFrames.length - 1) return last;
  const t = prevFrames[prevIndex];
  const i = lastIndexAtOrBefore(nextFrames, t);
  return i < 0 ? 0 : i;
}

/**
 * Quadros da janela decodificada, em ordem de prioridade (o atual primeiro):
 * - tocando: os próximos na ordem de reprodução (com a volta ao início no
 *   "repetir" e a inversão nas pontas no "vai e volta");
 * - parado: vizinhos dos dois lados (atual, +1, −1, +2, −2…), para quem anda
 *   quadro a quadro.
 */
export function decodeOrder(
  index: number,
  count: number,
  size: number,
  opts: { playing: boolean; mode: 'loop' | 'bounce'; direction: 1 | -1 },
): number[] {
  if (count <= 0) return [];
  const n = Math.min(size, count);
  const out: number[] = [];
  const seen = new Set<number>();
  const push = (i: number) => {
    const j = ((i % count) + count) % count;
    if (!seen.has(j)) {
      seen.add(j);
      out.push(j);
    }
  };
  push(index);
  if (opts.playing) {
    let cur = { index, direction: opts.direction };
    for (let guard = 0; out.length < n && guard < count * 4; guard++) {
      cur = nextPlaybackIndex(cur.index, count, cur.direction, opts.mode);
      push(cur.index);
    }
  } else {
    for (let d = 1; out.length < n && d <= count; d++) {
      push(index + d);
      if (out.length < n) push(index - d);
    }
  }
  return out.slice(0, n);
}

/** Próximo índice da reprodução, considerando o modo (repetir / vai e volta). */
/**
 * Quadros que passam pela decodificação quando o loop não cabe inteiro na
 * janela: dois lugares, o do quadro na tela e o do próximo a chegar.
 */
export const STREAM_SLOTS = 2;

/**
 * Quadros FIXOS de uma janela menor que o loop: `slots − STREAM_SLOTS`
 * quadros ficam decodificados o tempo todo, e os demais (espalhados pelo
 * loop, nunca o último, que é o mais visto) passam por dois lugares
 * rotativos. Vazio quando o loop cabe inteiro.
 */
export function residentFrames(count: number, slots: number): Set<number> {
  if (slots >= count) return new Set(Array.from({ length: count }, (_, i) => i));
  const resident = Math.max(0, slots - STREAM_SLOTS);
  const streamed = count - resident;
  const out = new Set<number>(Array.from({ length: count }, (_, i) => i));
  // Os que passam ficam espalhados (e não em bloco): o tempo para decodificar
  // cada um é o intervalo entre eles, e não um quadro só.
  const span = Math.max(1, count - 1);
  for (let j = 0; j < streamed; j++) out.delete(Math.min(span - 1, Math.floor(((j + 0.5) * span) / streamed)));
  // Arredondamentos podem repetir um índice: completa pelos que sobraram.
  for (let i = span - 1; out.size > resident && i >= 0; i--) out.delete(i);
  if (out.size > resident) out.delete(count - 1);
  return out;
}

/**
 * Janela decodificada, em ordem de prioridade (o atual primeiro).
 *
 * Com o loop inteiro cabendo, é o loop todo na ordem de `decodeOrder`. Sem
 * caber, a janela antiga DESLIZAVA: a cada passo um quadro saía e outro
 * entrava, e cada volta do loop decodificava todos os quadros de novo (um
 * JPEG de 7200 px por passo, ~0,3 s de processador). Aqui a maior parte fica
 * fixa (`residentFrames`) e só os outros passam pelos dois lugares rotativos:
 * cada volta decodifica `count − fixos` quadros, e não `count`. É o que deixa
 * usar um degrau mais nítido pagando pouco processador.
 */
export function decodeWindow(
  index: number,
  count: number,
  slots: number,
  opts: { playing: boolean; mode: 'loop' | 'bounce'; direction: 1 | -1 },
): number[] {
  if (count <= 0) return [];
  const order = decodeOrder(index, count, count, opts);
  if (slots >= count) return order;
  const resident = residentFrames(count, slots);
  const out: number[] = [];
  let streamed = 0;
  for (const i of order) {
    if (resident.has(i)) out.push(i);
    else if (streamed < STREAM_SLOTS) {
      out.push(i);
      streamed++;
    }
  }
  return out;
}

export function nextPlaybackIndex(
  index: number,
  count: number,
  direction: 1 | -1,
  mode: 'loop' | 'bounce',
): { index: number; direction: 1 | -1 } {
  if (count <= 1) return { index: 0, direction };
  if (mode === 'loop') return { index: (index + 1) % count, direction: 1 };
  let dir = direction;
  let next = index + dir;
  if (next >= count || next < 0) {
    dir = dir === 1 ? -1 : 1;
    next = index + dir;
  }
  return { index: next, direction: dir };
}
